import {
  CourseModuleModuleComponent,
  CourseModuleResponseDTO
} from '@/jbi-shared/dto/jtms.dtos';
import { CourseModuleStatus } from '@/jbi-shared/types/jtms.types';
import { CourseModuleComponentItem } from '@/store/modules/jtms/course-module/course-module.types';

/**
 * Converts frontend specific payload (used as simplified rendering purposes)
 * into backend-ready payload for create/update operations on course modules.
 * @param courseModuleComponentItem array of items to transform
 * @returns array of transformed items
 */
export function convertCourseModuleComponentItem(
  courseModuleComponentItem: CourseModuleComponentItem[],
  isDeleteArray: boolean = false
): CourseModuleModuleComponent[] {
  const transformedItems = courseModuleComponentItem.map<CourseModuleModuleComponent>(
    (courseModule) => ({
      id: courseModule.id ? courseModule.id : undefined,
      tempId: courseModule.tempId!,
      moduleId: courseModule.module?.id!,
      trainerIds:
        courseModule.trainers && courseModule.trainers.length > 0
          ? courseModule.trainers.map((trainer) => trainer.id)
          : [],
      startDate: courseModule.startDate!,
      endDate: courseModule.endDate!,
      prerequisiteIds: courseModule.prerequisiteIds ?? [],
      note: courseModule.note ?? '',
      status:
        courseModule.status && isDeleteArray
          ? CourseModuleStatus.INACTIVE
          : CourseModuleStatus.ACTIVE
    })
  );

  return transformedItems;
}

/**
 * Converts API Response object from backend into frontend payload.
 * @param courseModuleResponseDTO array of items to transform
 * @returns array of transformed items
 */
export function convertCourseModuleResponseDTO(
  courseModuleResponseDTO: CourseModuleResponseDTO[],
  shouldDisableDropdown: boolean = false,
  requiredIds?: number[]
): CourseModuleComponentItem[] {
  const transformedItems = courseModuleResponseDTO.map<CourseModuleComponentItem>(
    (courseModule) => ({
      id: courseModule.id ? courseModule.id : undefined,
      tempId: courseModule.id!,
      moduleId: courseModule.module?.id!,
      trainerIds:
        courseModule.trainers && courseModule.trainers.length > 0
          ? courseModule.trainers.map((trainer) => trainer.id)
          : [],
      startDate: courseModule.startDate!,
      endDate: courseModule.endDate!,
      prerequisiteIds: courseModule.prerequisites
        ? courseModule.prerequisites.map((prereq) => {
            return {
              id: prereq.id!,
              isExistingCourseModule: true
            };
          })
        : [],
      note: courseModule.note ?? '',
      status: courseModule.status,
      requiredByIds: requiredIds ? requiredIds : [],
      hasFieldError: false, // Existing course modules have no field errors by default
      shouldDisablePrerequisiteDropdown: shouldDisableDropdown
    })
  );

  return transformedItems;
}

/**
 * Recursively checks if a module is a descendant (prerequisite) of another module
 * in a dependency chain. This is used to detect circular dependencies.
 *
 * @param targetModuleId - The ID of the module we are trying to reach (the potential circular dependency).
 * @param currentModuleId - The ID of the module we are currently inspecting in the chain.
 * @param allModules - The complete list of all active modules.
 * @param visited - A Set to keep track of visited modules to prevent infinite loops in case of existing cycles.
 * @returns `true` if a path from currentModuleId to targetModuleId is found, `false` otherwise.
 */
export function isDescendant(
  targetModuleId: number,
  currentModuleId: number,
  allModules: CourseModuleComponentItem[],
  visited: Set<number> = new Set()
): boolean {
  // If we've already visited this node in this traversal, we're in a loop.
  if (visited.has(currentModuleId)) {
    return false;
  }
  visited.add(currentModuleId);

  const currentModule = allModules.find((m) => m.tempId === currentModuleId);
  if (
    !currentModule ||
    !currentModule.prerequisiteIds ||
    currentModule.prerequisiteIds.length === 0
  ) {
    // This module has no prerequisites, so it's the end of this path.
    return false;
  }

  // Check if any of the direct prerequisites match the target.
  if (currentModule.prerequisiteIds.some((p) => p.id === targetModuleId)) {
    return true; // Found a direct path.
  }

  // If not found directly, recursively check each prerequisite's own prerequisites.
  return currentModule.prerequisiteIds.some((p) =>
    isDescendant(targetModuleId, p.id as number, allModules, visited)
  );
}

// Generates a random negative int
export function generateTempId() {
  return Math.floor(Math.random() * 999 * -1);
}

export function formatModuleLabel(modules: string[]): string {
  // 1. Handle empty or single items
  if (!modules || modules.length === 0) {
    return '';
  }

  // 2. Handle Single Item -> Append "Only"
  if (modules.length === 1) {
    return `${modules[0]} Only`;
  }

  // 3. Check if all items share the "Module " prefix
  const prefix = 'Module ';
  const hasPrefix = modules.every((m) => m.startsWith(prefix));

  if (hasPrefix) {
    // Extract just the numbers/suffixes (e.g., ['1', '2', '3'])
    const suffixes = modules.map((m) => m.replace(prefix, ''));

    // Pop the last item to handle the ampersand (&)
    const last = suffixes.pop();
    const joinedSuffixes =
      suffixes.length > 0 ? `${suffixes.join(', ')} & ${last}` : last;

    // Return with PLURAL "Modules"
    return `Modules ${joinedSuffixes}`;
  }

  // 4. Fallback for non-standard strings (e.g. ['Intro', 'Conclusion'])
  // Result: "Intro & Conclusion"
  const clone = [...modules]; // Clone to avoid mutating prop
  const last = clone.pop();
  return `${clone.join(', ')} & ${last}`;
}
